import React, { useState, useEffect, useRef, useContext } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { setSpace, setNewMessage } from "../../../../store/app";
import Gleap from 'gleap';

import {
  Container,
  Stack,
  ScrollArea,
  Drawer,
  Box,
  createStyles,
} from "@mantine/core";

import { storeJSON, getJSON } from "../../../../utils/LocalDB";
import NotificationModel from "../../../../../models/NotificationModel";
import MessageModel from "../../../../models/Message";
import TagModel from "../../../../models/Tag";
import SpaceModel from "../../../../models/Space";
import { scrollToElement } from "../../../../utils/ScrollToElement";
import Message from "./Message";
import Reflections from "./Reflections";

import TiptapSimple from "../../../tiptap/components/TiptapSimple";
import { ActionCableContext } from "../../../../components/Index";
import HeaderImage from "../../HeaderImage";
import FixedHeader from "../../FixedHeader";

import { useTranslation } from "react-i18next";

import trackMixpanel from "../../../../utils/TrackMixpanel";

const paginator = {
  per: 30,
  page: 1,
};

const queryParams = {
  pod_message_id: null,
  reflection_id: null,
};

const scrollPosition = {
  x: 0,
  y: 0,
};

const useStyles = createStyles((theme, { colors }, getRef) => ({
  drawer: {
    backgroundColor: colors.background_color,
  },
  drawerHeader: {
    color: colors.default_text_color,
    fontWeight: 700,
    paddingBottom: 10,
    // borderBottom: `1px solid ${colors.default_text_color}`
  },
  spaceContainer: {
    backgroundColor: colors.background_color,
    color: colors.default_text_color,
    ".ProseMirror a": {
      color: colors.default_link_color,
    },
    ".ProseMirror .fancylink-node .main-content": {
      color: colors.sidebar_text_color,
      backgroundColor: colors.sidebar_background_color,
      borderRadius: "5px",
    },
  },
  closeButton: {
    backgroundColor: colors.background_color,
    color: colors.default_text_color,
    "&:hover": {
      backgroundColor: colors.background_color,
      color: colors.primary_button_background_color,
    },
  },
}));

export default function Discussion(props) {
  // const { ref, width, height } = useElementSize();
  const colors = useSelector((state) => state.colors);
  const { classes, cx, theme } = useStyles({ colors });
  const { t } = useTranslation();

  const viewport = useRef();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const cable = useContext(ActionCableContext);

  const currentLocation = useLocation();

  const params = useParams();

  const user = useSelector((state) => state.user);
  const space = useSelector((state) => state.space);
  const mainActions = useSelector((state) => state.mainActions);

  const [openedReflections, setOpenedReflections] = useState(false);
  const [messages, setMessages] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [receiveData, setReceiveData] = useState(null);
  const [tagging, setTagging] = useState(0);
  const [reflectionsTagging, setReflectionsTagging] = useState(false);
  const [reflectionsDrawerHeader, setReflectionsDrawerHeader] = useState(
    t("spaces.presentations.discussion.reflectionHeader")
  );
  const [tag, setTag] = useState([]);
  const [tags, setTags] = useState([]);

  const pileIntervalId = useRef(null);
  const channelPile = useRef([]);
  const messagesCopy = useRef([]);
  const currentConnectedSlug = useRef();
  const scrollToBottomAfter = useRef();
  const atBottom = useRef(false);
  const scrollTimeoutIds = useRef([]);
  const connectionIntervalId = useRef([]);
  const lastConnectionAttempt = useRef({
    slug: null,
    state: null,
    channel: null,
  });
  const paramsChangeTimeout = useRef([]);
  const tagFetch = useRef(false);
  const querySearchCount = useRef(0);
  const loadingVariables = useRef(0);
  const tagsLoaded = useRef(0);

  const tiptapSidemenu = useSelector((state) => state.tiptapSidemenu);

  useEffect(() => {
    if (pileIntervalId.current) {
      clearInterval(pileIntervalId.current);
    }

    pileIntervalId.current = setInterval(() => {

      if (
        channelPile.current.length > 0 &&
        channelPile.current[0].state == "pending"
      ) {
        channelPile.current[0].state = "in_progress";

        channelPile.current[0].action(channelPile.current[0].id);
        return;
      }
      if (
        channelPile.current.length > 0 &&
        channelPile.current[0].state == "done"
      ) {
        channelPile.current.splice(0, 1);

        return;
      }
      if (
        channelPile.current.length > 0 &&
        channelPile.current[0].state == "in_progress"
      ) {
        return;
      }
    }, 1000);

    if (window.$currentUserAuthToken) {
      trackMixpanel(window.$currentUsername, "Discussion loaded");
    }

    return () => {
      setMessages([]);
      messagesCopy.current = [];

      Gleap.showFeedbackButton(true);

      if (lastConnectionAttempt.current.channel) {
        lastConnectionAttempt.current.channel.unsubscribe();
        lastConnectionAttempt.current.channel = null;
        lastConnectionAttempt.current.slug = null;
        lastConnectionAttempt.current.state = null;
      }
      clearInterval(pileIntervalId.current);
      clearTimeout(connectionIntervalId.current);

      scrollToBottomAfter.current = false;
      loadingVariables.current = false;
    };
  }, []);

  useEffect(() => {
    if (user && user.organic == "direct") {
      Gleap.showFeedbackButton(false);
    }
    return () => {
      if (user && user.organic == "direct") {
        Gleap.showFeedbackButton(true);
      }
    }
  }, [user]);

  useEffect(() => {
    if (tiptapSidemenu && tiptapSidemenu.opened == "reflections") {
      setOpenedReflections(true);
    } else {
      setOpenedReflections(false);
    }
  }, [tiptapSidemenu]);

  useEffect(() => {
    clearTimeout(connectionIntervalId.current);
    if (params && params.slug) {
      if (paramsChangeTimeout.current) {
        clearTimeout(paramsChangeTimeout.current);
      }
      paramsChangeTimeout.current = setTimeout(() => {
        cableConnect();
      }, 300);

      onFetchTags();
    }
  }, [params]);

  useEffect(() => {
    if (lastConnectionAttempt.current.state == "connected") {
      querySearchCount.current = 0;
      checkSearchParams();
      onFetchMessages(true);
      setTimeout(() => {
        markAsSeen();
      }, 5000);
    }
  }, [location.search]);

  useEffect(() => {}, [props]);

  useEffect(() => {
    if (receiveData) {
      let changes = receiveData.changes;
      if (receiveData.state != "multi") {
        changes = [receiveData];
      }

      for (var i = 0; i < changes.length; i++) {
        if (changes[i].state == "new") {
          channelPile.current.push({
            id: changes[i].pod_message_id,
            action: onReceivedMessage,
            state: "pending",
          });
          // onReceivedMessage(changes[i].pod_message_id);
        } else if (changes[i].state == "update") {
          channelPile.current.push({
            id: changes[i].pod_message_id,
            action: onUpdatedMessage,
            state: "pending",
          });
          // onUpdatedMessage(changes[i].pod_message_id);
        } else if (changes[i].state == "destroy") {
          channelPile.current.push({
            id: changes[i].pod_message_id,
            action: onDestroyedMessage,
            state: "pending",
          });
          // onDestroyedMessage(changes[i].pod_message_id);
        } else if (changes[i].state == "pin") {
          channelPile.current.push({
            id: changes[i].pod_message_id,
            action: onPinnedMessage,
            state: "pending",onFetchMessages
          });
          // onPinnedMessage(changes[i].pod_message_id);
        } else if (changes[i].state == "unpin") {
          channelPile.current.push({
            id: null,
            action: onPinnedMessage,
            state: "pending",
          });
          // onPinnedMessage(null);
        }
      }
    }
  }, [receiveData]);

  useEffect(() => {
    if (channelPile.current && channelPile.current.length > 0) {
      return;
    }

    if (scrollToBottomAfter.current == true) {
      scrollToBottomAfter.current = false;

      scrollToBottom();
      return;
    }

    if (!loadingVariables.current && messages.length > 0) {
      loadingVariables.current = true;
      if (!queryParams.pod_message_id) {
        // setIsFirstLoad(true);
        scrollToBottom();
        return;
      }
    }

    if (queryParams.pod_message_id && messages.length > 0) {
      searchForMessage(queryParams.pod_message_id);
    }
  }, [messages]);

  useEffect(() => {
    paginator.page = 1;
    if (tagsLoaded.current > 0){
      onFetchMessages();
    }
    else{
      setTimeout(() => {
        tagsLoaded.current = 1
      }, 1000)
    }
  }, [tag]);

  const checkSearchParams = () => {
    const search = currentLocation.search;
    const pod_message_id = new URLSearchParams(search).get("pod_message_id");
    const reflection_id = new URLSearchParams(search).get("reflection_id");
    const _tag = new URLSearchParams(search).get("tag");
    if (pod_message_id || reflection_id) {
      queryParams.pod_message_id = pod_message_id;
      queryParams.reflection_id = reflection_id;
    }

    if (_tag) {
      setTag([_tag]);
    } else {
      setTag([]);
    }

    if (
      messages.length > 0 &&
      messages[0].pod_slug == params.slug &&
      queryParams.pod_message_id
    ) {
      searchForMessage(queryParams.pod_message_id);
    }
  };

  const cableDisconnectIfNeeded = () => {
    if (
      cable &&
      lastConnectionAttempt.current.channel &&
      lastConnectionAttempt.current.slug != params.slug
    ) {
      lastConnectionAttempt.current.state = "disconnecting";
      // debugger;
      console.log(
        "ACTION CABLE: disconnecting - " + lastConnectionAttempt.current.slug
      );
      setTimeout(() => {
        console.log(
          "ACTION CABLE: disconnected 1 - " + lastConnectionAttempt.current.slug
        );
        lastConnectionAttempt.current.channel = null;
        lastConnectionAttempt.current.state = null;
        cableConnect();
      }, 100);
      lastConnectionAttempt.current.channel.unsubscribe();
    } else {
    }
  };

  const cableConnect = () => {
    cableDisconnectIfNeeded();

    if (lastConnectionAttempt.current.state == "disconnecting") {
      console.log(
        "ACTION CABLE: disconnecting skipped - " +
          lastConnectionAttempt.current.slug
      );
      return;
    }

    if (
      lastConnectionAttempt.current.slug == params.slug &&
      lastConnectionAttempt.current.state == "connected"
    ) {
      console.log(
        "ACTION CABLE: skipping connection - " +
          lastConnectionAttempt.current.slug
      );
      return;
    }

    lastConnectionAttempt.current.slug = params.slug;
    lastConnectionAttempt.current.state = "connecting";

    performCableConnection();

    connectionIntervalId.current = setTimeout(() => {
      if (lastConnectionAttempt.current.state == "connecting") {
        console.log("ACTION CABLE: forcing messages fetch 2 - " + params.slug);
        onFetchMessages(true);
      }
    }, 1000);
  };

  const performCableConnection = () => {
    console.log(
      "ACTION CABLE: connection attempt " + lastConnectionAttempt.current.slug
    );
    lastConnectionAttempt.current.channel = cable.subscriptions.create(
      {
        channel: "PodsChannel",
        pod_slug: params.slug,
        token: window.$currentUserAuthToken,
      },
      {
        connected: () => {
          console.log(
            `ACTION CABLE: connection data: ${currentConnectedSlug.current} == ${params.slug}`
          );

          if (lastConnectionAttempt.current.state == "connecting") {
            console.log(
              "ACTION CABLE: connected completed - " +
                lastConnectionAttempt.current.slug
            );
            lastConnectionAttempt.current.state = "connected";
            checkSearchParams();
            setTimeout(() => {
              markAsSeen();
            }, 5000);
            onFetchMessages(true);
            return;
          }
          console.log("ACTION CABLE: messages.length - " + messages.length);
          console.log(
            "ACTION CABLE: message slug - " + messages.length > 0 &&
              messages[0].pod_slug
          );
          if (
            messages.length == 0 ||
            (messages.length > 0 &&
              messages[0].pod_slug != lastConnectionAttempt.current.slug)
          ) {
            console.log(
              "ACTION CABLE: forcing messages fetch - " + params.slug
            );
            onFetchMessages(true);
          }
          console.log(
            "ACTION CABLE: connected skipped - " +
              lastConnectionAttempt.current.slug
          );
        },
        disconnected: () => {
          console.log(
            "ACTION CABLE: disconnected 2 - " +
              lastConnectionAttempt.current.slug
          );
        },
        received: (data) => {
          console.log(
            "ACTION CABLE: received - " + lastConnectionAttempt.current.slug
          );
          console.log(
            "ACTION CABLE: data - " +
              JSON.stringify(data) +
              " - params: " +
              params.slug +
              " - current: " +
              lastConnectionAttempt.current.slug
          );
          if (data.slug == lastConnectionAttempt.current.slug) {
            paginator.newMessage = true;
            setReceiveData(data);
            return;
          }
          console.log(
            "ACTION CABLE: received ignored - " +
              lastConnectionAttempt.current.slug
          );
        },
      }
    );
  };

  const markAsSeen = () => {
    NotificationModel.onMarkAsSeen("pod_message", params.slug, (data) => {});
  };

  const markReflectionAsSeen = (messageId) => {
    NotificationModel.onMarkAsSeen("reflection", messageId, (data) => {});
  };

  const onFetchMessages = (reset = false) => {
    if (channelPile.current && channelPile.current.length > 0) {
      return;
    }
    if (reset && messages.length > 0 && messages[0].pod_slug == params.slug) {
      return;
    }

    const search = window.location.search;
    let _tag = new URLSearchParams(search).get("tag");
    const _pod_message_id = new URLSearchParams(search).get("pod_message_id");

    if (reset) {
      paginator.page = 1;
      messagesCopy.current = [];
      setMessages([]);
      loadingVariables.current = false;
    }

    if (_tag && tagFetch.current != _tag) {
      tagFetch.current = _tag;
      paginator.page = 1;
      messagesCopy.current = [];
      setMessages([]);
      loadingVariables.current = false;
    }

    if (tag && JSON.stringify(tag) != JSON.stringify(tagFetch.current)) {
      tagFetch.current = tag
      paginator.page = 1;
      messagesCopy.current = [];
      setMessages([]);
      loadingVariables.current = false;
      
    }
    if (tag){
      _tag = tag;
    }

    if (!_tag && !_pod_message_id && paginator.page == 1) {
      if (!queryParams.pod_message_id) {
        scrollToBottomAfter.current = true;
      }

      getJSON(`spaces`, `messages-${params.slug}`, (data) => {
        if (data && data.data.length > 0) {
          setMessages([...data.data]);
          messagesCopy.current = [...data.data];
          onFetchMessagesFromAPI(_tag, data.stored_at, data.data);
        } else {
          onFetchMessagesFromAPI(_tag);
        }
      });
    } else {
      onFetchMessagesFromAPI(_tag);
    }
  };

  const onFetchMessagesFromAPI = (_tag, storedAt = null, stored = null) => {
    if (_tag) {
      trackMixpanel(window.$currentUsername, "Tag clicked", { space_slug: params.slug }); 
    }
    MessageModel.onFetch(
      params.slug,
      { tag: _tag },
      paginator.page,
      (data) => {
        let lookForMessage = true;
        if (data.data.length > 0 && paginator.page > 1) {
          setMessages(data.data.concat(messages));
          messagesCopy.current = [...data.data.concat(messages)];
          lookForMessage = false;
        } else {
          if (paginator.page == 1) {
            storeJSON("spaces", `messages-${params.slug}`, { ...data });
          }

          if (data.data.length > 0 && paginator.page == 1) {
            if (
              !storedAt ||
              (storedAt &&
                anyFetchedMessageChanged(data.data, stored, storedAt))
            ) {
              console.log("SILENT UPDATE DONE MESSAGES");
              setMessages([...data.data]);
              messagesCopy.current = [...data.data];
              lookForMessage = false;
            }
          } else if (paginator.page == 1) {
            setMessages([...data.data]);
            messagesCopy.current = [...data.data];
            lookForMessage = false;
          }
        }
        if (data.data.length == 0 && paginator.page > 1) {
          paginator.page--;
        }
        if (
          lookForMessage &&
          queryParams.pod_message_id &&
          messages.length > 0
        ) {
          searchForMessage(queryParams.pod_message_id);
        }
        window.$headerIntervalActive = true
      },
      (data) => {}
    );
  };

  const anyFetchedMessageChanged = (fetched, stored, storedAt) => {
    for (let i = 0; i < fetched.length; i++) {
      const objectA = fetched[i];
      const objectB = stored.find((obj) => obj.id === objectA.id);
      if (!objectB) {
        if (!atBottom.current) {
          dispatch(setNewMessage({ show: true, viewport: viewport.current }));
        }
        return true;
      }
      if (
        objectB &&
        (new Date(objectA.updated_at) > new Date(objectB.updated_at) ||
          objectA.reflections.length > objectB.reflections.length)
      ) {
        // display bar

        if (!atBottom.current) {
          dispatch(setNewMessage({ show: true, viewport: viewport.current }));
        }
        return true;
      }
    }

    for (let i = 0; i < stored.length; i++) {
      const objectA = stored[i];
      const objectB = fetched.find((obj) => obj.id === objectA.id);
      if (!objectB) {
        if (!atBottom.current) {
          dispatch(setNewMessage({ show: true, viewport: viewport.current }));
        }
        return true;
      }
      if (
        objectB &&
        (new Date(objectB.updated_at) > new Date(objectA.updated_at) ||
          objectB.reflections.length > objectA.reflections.length)
      ) {
        // display bar

        if (!atBottom.current) {
          dispatch(setNewMessage({ show: true, viewport: viewport.current }));
        }
        return true;
      }
    }

    return false;
  };

  const searchForMessage = (messageId) => {
    if (querySearchCount.current > 10) {
      return;
    }

    querySearchCount.current++;

    for (let i = 0; i < messages.length; i++) {
      if (messages[i].id == messageId) {
        queryParams.pod_message_id = null;
        querySearchCount.current = 99;

        scrollToMessage(messageId);
        if (queryParams.reflection_id) {
          const newMessage = { ...messages[i] };
          newMessage.reflectionId = queryParams.reflection_id;
          queryParams.reflection_id = null;
          setSelectedMessage(newMessage);
          setOpenedReflections(true);
          markReflectionAsSeen(messageId);
          setReflectionsDrawerHeader(
            t("spaces.presentations.discussion.reflectionHeader")
          );
        }
        return;
      }
    }
    paginator.page++;
    onFetchMessages();
  };

  const onReceivedMessage = (messageId) => {
    MessageModel.onFetchById(
      messageId,
      space.slug,
      (data) => {
        if (data.user.id != window.$currentUserId) {
          if (atBottom.current) {
            loadingVariables.current = false;
            scrollToBottomAfter.current = true;

            setTimeout(() => {
              scrollToBottom();
            }, 500);
          } else {
            //Trigger new message
            dispatch(setNewMessage({ show: true, viewport: viewport.current }));
          }
        } else {
          loadingVariables.current = false;
          scrollToBottomAfter.current = true;
          setTimeout(() => {
            scrollToBottom();
          }, 500);
        }

        let newMessages = [...messagesCopy.current];

        if (messagesCopy.current.length == 0) {
          messagesCopy.current = [...messages];
          newMessages = [...messagesCopy.current];
        }

        newMessages.push(data);
        messagesCopy.current.push(data);

        setMessages(newMessages);
        onRemovePileAction();
      },
      (data) => {
        onRemovePileAction();
      }
    );
  };

  const onUpdatedMessage = (messageId) => {
    MessageModel.onFetchById(
      messageId,
      space.slug,
      (data) => {
        loadingVariables.current = true;
        const newMessages = [...messages];

        for (let i = 0; i < newMessages.length; i++) {
          if (newMessages[i].id == messageId) {
            newMessages[i] = data;
            if (selectedMessage && messageId == selectedMessage.id) {
              setSelectedMessage({ ...newMessages[i] });
            }
            break;
          }
        }

        setMessages(newMessages);
        messagesCopy.current = [...newMessages];
        onRemovePileAction();
      },
      (data) => {
        onRemovePileAction();
      }
    );
  };

  const onDestroyedMessage = (messageId) => {
    const newMessages = [...messages];

    for (let i = 0; i < newMessages.length; i++) {
      if (newMessages[i].id == messageId) {
        newMessages.splice(i, 1);
        break;
      }
    }

    setMessages(newMessages);
    messagesCopy.current = [...newMessages];
    onRemovePileAction();
  };

  const onPinnedMessage = (messageId) => {
    const newSpace = { ...space };
    newSpace.pin_message = messageId;
    dispatch(setSpace(newSpace));
    onRemovePileAction();
  };

  const onRemovePileAction = () => {
    if (channelPile.current.length > 0) {
      channelPile.current[0].state = "done";
    }
  };

  const scrollToReflection = (message) => {
    scrollToElement(`reflection_${id}`);
  };

  const onScrollTop = () => {
    paginator.page++;
    onFetchMessages();
  };

  const onSave = (json, html) => {
    if (!space.permissions.is_member){
      onJoin()
    }
    json.attrs = { uid: `message-${new Date().getTime()}` };
    json.type = "paragraph";
    MessageModel.onCreate(
      params.slug,
      { json, html },
      (data) => {
        storeJSON("spaces", `discussion-input-${params.slug}`, {
          json: "",
          html: "",
        });
        if (props.onSave) {
          props.onSave();
        }
      },
      (data) => {}
    );
  };

  const onJoin = () => {
    
    if (!user || (user && !user.id)) {
 
      localStorage.setItem("join_slug", space.slug);
      navigate(`/user/start?s=${space.slug}`);
      return;
    }

    SpaceModel.onJoin(
      space.slug,
      (data) => {
        navigate(`${data.redirect_to.url}`);
      },
      (data) => {}
    );
  };

  const onOpenReflections = (messageId) => {
    setOpenedReflections(true);
    setReflectionsDrawerHeader(
      t("spaces.presentations.discussion.reflectionHeader")
    );

    for (let i = 0; i < messages.length; i++) {
      if (messages[i].id == messageId) {
        markReflectionAsSeen(messageId);
        setSelectedMessage(messages[i]);
        break;
      }
    }
  };

  const scrollToBottom = () => {
    if (viewport.current) {
    }
    setTimeout(() => {
      if (viewport.current) {
        viewport.current.scrollTop = viewport.current.scrollHeight;
        const top = viewport.current.scrollHeight + 2000;
        viewport.current.scrollTo({ top: top, behavior: "instant" });
      }
    }, 100);
  };

  const scrollToMessage = (messageId) => {
    setTimeout(() => {
      try {
        const $scrollTo = document.getElementById(`msg_${messageId}`);
        const top =
          $scrollTo.offsetTop -
          viewport.current.offsetTop +
          viewport.current.scrollTop +
          80;

        viewport.current.scrollTo({ top: top, behavior: "instant" });
      } catch (error) {}
    }, 500);
  };

  const scrollToTop = () => {
    viewport.current.scrollTo({ top: 0, behavior: "instant" });
  };

  const onViewPinnedMessage = (message) => {
    setSelectedMessage(message);
    setOpenedReflections(true);
    setReflectionsDrawerHeader(
      t("spaces.presentations.discussion.pinnedMessageHeader")
    );
  };

  const onScrollPositionChange = (pos) => {
    const div = viewport.current;

    clearScrollTimeouts();

    if (pos.y + div.clientHeight >= div.scrollHeight - 50) {
      const scrollTimeoutId = setTimeout(() => {
        dispatch(setNewMessage({ show: false }));
        atBottom.current = true;
      }, 1000);
      scrollTimeoutIds.current.push(scrollTimeoutId);
    } else if (pos.y == 0) {
      atBottom.current = false;

      const scrollTimeoutId = setTimeout(() => {
        // At top
        onScrollTop();
      }, 300);
      scrollTimeoutIds.current.push(scrollTimeoutId);
    } else {
      // In between
      atBottom.current = false;
    }
  };

  const clearScrollTimeouts = () => {
    for (let i = 0; i < scrollTimeoutIds.current.length; i++) {
      const id = scrollTimeoutIds.current[i];
      clearTimeout(id);
    }
  };

  const onFetchTags = () => {
    TagModel.onFetch(
      params.slug,
      (data) => {
        setTags([...data]);
      },
      (data) => {}
    );
  };

  // if (props.loading) {
  //   return <Loading type="discussion" />;
  // }

  return (
    <Stack
      className={classes.spaceContainer}
      spacing={0}
      style={{ height: "100%" }}
    >
      {props.showHeader && messages.length > 0 && (
        <Box
          sx={{
            position: "relative",
          }}
        >
          <FixedHeader
            isMain={true}
            show={false}
            editable={mainActions.editPage}
            onViewPinnedMessage={onViewPinnedMessage}
            components={[
              "breadcrumbs",
              "title",
              "pinMessage",
              "calendar",
              "status",
              "tags",
              "new_message",
              "new_reflections",
              "topActions"
            ]}
            selectedTag={tag}
            tagsList={tags}
            setTags={setTag}
          />
        </Box>
      )}
      <ScrollArea
        id="main-scroll-area"
        onScrollPositionChange={onScrollPositionChange}
        className="content-scrollview"
        viewportRef={viewport}
        style={{
          height: "100%",
          display: "flex",
          flexDirection: "column-reverse",
        }}
      >
        {props.showHeader && (
          <>
            <HeaderImage />
            <FixedHeader
              isMain={false}
              show={true}
              inverted={true}
              editable={mainActions.editPage}
              onViewPinnedMessage={onViewPinnedMessage}
              components={[
                "breadcrumbs",
                "title",
                "pinMessage",
                "calendar",
                "status",
                "tags",
                "new_message",
                "new_reflections",
                "topActions"
              ]}
              selectedTag={tag}
              tagsList={tags}
              setTags={setTag}
            />
          </>
        )}
        {messages &&
          messages.length > 0 &&
          messages[0].pod_slug == params.slug && (
            <Container
              size={space.space_width == "wide" ? 960 : 801}
              sx={(theme) => ({
                height: "100%",
                paddingBottom: "15px",
              })}
            >
              {messages.map((message) => {
                return (
                  <Message
                    key={`message_${message.id}`}
                    spaceId={space.slug}
                    spaceVersion={space.version}
                    messageId={message.id}
                    htmlId={`${message.type}_${message.id}`}
                    messageCreatedAt={message.created_at}
                    messageTitle={message.post ? message.post.subject : null}
                    messageContent={
                      message.version == "new"
                        ? message.content
                        : message.message_body
                    }
                    messageUnseenCount={message.unseen_count}
                    tags={message.tags}
                    reflections={message.reflections}
                    user={message.user ? message.user : {}}
                    onOpenReflections={onOpenReflections}
                    version={message.version}
                    canPin={
                      message.user
                        ? message.user.can_pin && !space.is_direct
                        : false
                    }
                    canTag={
                      message.user
                        ? message.user.can_tag && !space.is_direct
                        : false
                    }
                    canLike={message.user ? message.user.can_like : false}
                    canReflect={
                      message.user
                        ? message.user.can_reflect && !space.is_direct
                        : false
                    }
                    canEdit={message.user ? message.user.can_edit : false}
                    canDelete={message.user ? message.user.can_delete : false}
                    onUpdated={onUpdatedMessage}
                    type={"pod_message"}
                    tagging={tagging}
                    setTagging={setTagging}
                    isPinned={space.pin_message == message.id}
                    text={message.text}
                    isLiked={message.is_liked}
                    likesCount={message.likes_count}
                    canCreateTags={space.settings.allow_members_to_create_tags}
                  />
                );
              })}
            </Container>
          )}
      </ScrollArea>
      {space &&
        space.permissions &&
        !space.permissions.read_only && (
          <Box>
            <Container size={space.space_width == "wide" ? 960 : 801} mb={20}>
              <TiptapSimple
                editable={true}
                onSave={onSave}
                modals={true}
                contextDb={`discussion-input-${params.slug}`}
              />
            </Container>
          </Box>
        )}
      {space && space.permissions ? (
        <Drawer
          opened={openedReflections}
          onClose={() => setOpenedReflections(false)}
          title={reflectionsDrawerHeader}
          padding="xl"
          size={600}
          classNames={{
            drawer: classes.drawer,
            header: classes.drawerHeader,
            closeButton: classes.closeButton,
          }}
        >
          <Reflections
            spaceId={space.slug}
            spaceVersion={props.spaceVersion}
            canReflect={space.permissions.allow_reflections}
            message={selectedMessage}
            onUpdatedMessage={onUpdatedMessage}
            tagging={reflectionsTagging}
            setTagging={setReflectionsTagging}
            canCreateTags={space.settings.allow_members_to_create_tags}
          />
        </Drawer>
      ) : (
        ""
      )}
    </Stack>
  );
}
